// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { ReactElement, ReactNode, memo, useEffect, useState } from 'react';
import { AttachmentType } from 'thehive-sdk';
import { formatFileSize } from '@Helpers/stats';
import { AttachmentParentType, GenericAttachmentType, usePreview } from '@Molecules/THPreview';
import client from '@Client';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import {
    FileExcelOutlined,
    FileJpgOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileZipOutlined,
} from '@ant-design/icons';
import { commentAttachmentContentTypes } from './commentAttachmentContentTypes';
import { ThumbnailIcon } from './ThumbnailIcon';
import { ThumbnailImage } from './ThumbnailImage';

interface CommentAttachmentProps {
    attachment: AttachmentType;
    parentId?: string;
    togglePreview?: (attachment: GenericAttachmentType, parent: AttachmentParentType) => void;
}

export const CommentAttachment = memo(
    ({ attachment, parentId = '', togglePreview }: CommentAttachmentProps): ReactElement => {
        const [fileKey, setFileKey] = useState('default');
        const { allowPreview } = usePreview();
        const { contentType, size, name, _id, id } = attachment;

        const thumbnails: Record<string, ReactElement | ReactNode> = {
            pdf: <ThumbnailIcon icon={<FilePdfOutlined />} />,
            doc: <ThumbnailIcon icon={<FileWordOutlined />} />,
            archive: <ThumbnailIcon icon={<FileZipOutlined />} />,
            imageValid: <ThumbnailImage id={id} contentType={contentType} />,
            imageInvalid: <ThumbnailIcon icon={<FileJpgOutlined />} />,
            text: <ThumbnailIcon icon={<FileTextOutlined />} />,
            excel: <ThumbnailIcon icon={<FileExcelOutlined />} />,
            default: <ThumbnailIcon icon={<FileOutlined />} />,
        };

        useEffect(() => {
            Object.keys(commentAttachmentContentTypes).find((key) => {
                if (commentAttachmentContentTypes[key].includes(contentType)) {
                    setFileKey(key);
                }
            });
        }, [contentType]);

        const attachmentUrl = `${window.location.origin}${client.apiUrl}/v1/log/${parentId}/attachment/${_id}/download`;

        return (
            <li className="Comment__Attachment">
                {thumbnails[fileKey]}

                <div className="Comment__Informations">
                    {allowPreview(contentType) && !!togglePreview ? (
                        <button
                            type="button"
                            className="Comment__Filename Comment__Filename--clickable"
                            onClick={() => togglePreview(attachment, { type: 'log', id: parentId })}
                        >
                            {name}
                        </button>
                    ) : (
                        <span className={'Comment__Filename'}>{name}</span>
                    )}

                    <span className="Comment__Details">
                        {contentType} - {formatFileSize(size)}
                    </span>
                </div>

                <ExternalLink className="Comment__Download" url={attachmentUrl}>
                    <ICONS.DownloadOutlined />
                </ExternalLink>
            </li>
        );
    },
);
